import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams, useNavigate } from "react-router-dom";
import { API } from "../lib/api";
import { SiteHeader } from "../components/SiteHeader";
import { ArrowLeft, Check, Loader2 } from "lucide-react";
import ImmersionIntake from "../components/ImmersionIntake";
import OrientationIntake from "../components/OrientationIntake";

const usd = (c) => (c == null ? null : `$${(c / 100).toFixed(2)}`);

export default function OfferingDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // immersion application state
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [applied, setApplied] = useState(false);
  const [intakeDone, setIntakeDone] = useState(false);
  const [orientationDone, setOrientationDone] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    setLoading(true); setNotFound(false);
    axios.get(`${API}/products/${slug}`).then(({ data }) => setP(data)).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [slug]);

  const apply = async (e) => {
    e.preventDefault(); setSending(true); setErr("");
    try {
      await axios.post(`${API}/offerings/immersion/apply`, form);
      setApplied(true);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Something interrupted the sending. Please try again.");
    } finally { setSending(false); }
  };

  if (loading) return <div className="flex min-h-[100dvh] items-center justify-center watercolor-wash"><Loader2 className="h-6 w-6 animate-spin text-ink-muted" /></div>;
  if (notFound || !p) return (
    <div className="flex min-h-[100dvh] items-center justify-center watercolor-wash px-5 text-center">
      <div><p className="text-ink-soft">That offering wasn't found.</p><Link to="/offerings" className="mt-3 inline-block text-terracotta underline">See all offerings</Link></div>
    </div>
  );

  const priceLine = p.is_application ? "By application"
    : p.is_recurring
      ? (p.intro_price_cents && p.intro_price_cents !== (p.standard_price_cents || p.price_cents)
          ? `${usd(p.intro_price_cents)} your first month, then ${usd(p.standard_price_cents || p.price_cents)}/month`
          : `${usd(p.standard_price_cents || p.price_cents)}/month${p.duration_months ? ` · ${p.duration_months} months` : ""}`)
    : `${usd(p.price_cents)} · one-time`;

  return (
    <div className="min-h-[100dvh]" style={{ background: "linear-gradient(180deg,#F3EFEA 0%,#ECE7DE 100%)" }} data-testid="offering-detail">
      <SiteHeader theme="light" />
      <div className="mx-auto max-w-2xl px-5 py-12 sm:px-8">
        <Link to="/offerings" data-testid="detail-back" className="mb-8 inline-flex items-center gap-2 font-display text-sm text-ink-soft hover:text-terracotta">
          <ArrowLeft className="h-4 w-4" /> All offerings
        </Link>
        <p className="kicker text-terracotta">Door {p.door_number} · {p.tagline}</p>
        <h1 className="mt-2 font-display text-4xl text-ink sm:text-5xl">{p.title}</h1>
        <p className="mt-4 text-[15px] leading-relaxed text-ink-soft">{p.description}</p>

        {p.narrative && (
          <div className="mt-8 overflow-hidden rounded-2xl bg-ivory-50 p-7 shadow-sm ring-1 ring-ivory-400/50 sm:p-9" data-testid="detail-narrative">
            {p.narrative.eyebrow && (
              <p className="text-[11px] uppercase tracking-[0.3em] text-gold-foil">{p.narrative.eyebrow}</p>
            )}
            {p.narrative.lead && (
              <p className="mt-4 font-display text-xl leading-snug text-ink sm:text-2xl">{p.narrative.lead}</p>
            )}
            <div className="mt-4 space-y-4">
              {(p.narrative.paragraphs || []).map((para, i) => (
                <p key={i} className="text-[15px] leading-relaxed text-ink-soft">{para}</p>
              ))}
            </div>
            {p.narrative.accent && (
              <p className="mt-4 font-display text-lg italic text-terracotta">{p.narrative.accent}</p>
            )}
            {Array.isArray(p.narrative.pillars) && p.narrative.pillars.length > 0 && (
              <div className="mt-7 grid gap-4 border-t border-ivory-400/60 pt-6 sm:grid-cols-3">
                {p.narrative.pillars.map((pl, i) => (
                  <div key={i} data-testid={`detail-narrative-pillar-${i}`}>
                    <p className="text-sm uppercase tracking-[0.18em] text-gold-foil">{pl.k}</p>
                    <p className="mt-1.5 text-[13px] leading-relaxed text-ink-soft">{pl.v}</p>
                  </div>
                ))}
              </div>
            )}
            {p.narrative.signoff && (
              <p className="mt-7 font-display text-sm italic text-ink-muted">{p.narrative.signoff}</p>
            )}
          </div>
        )}

        {Array.isArray(p.experience) && p.experience.length > 0 && (
          <div className="mt-7" data-testid="detail-experience">
            <p className="kicker text-gold-foil">What you will experience</p>
            <div className="mt-3 space-y-3">
              {p.experience.map((s, i) => (
                <div key={i} className="rounded-2xl bg-ivory-50 p-5 shadow-sm ring-1 ring-ivory-400/50" data-testid={`detail-experience-${i}`}>
                  <p className="font-display text-lg text-ink">{s.heading}</p>
                  <p className="mt-1.5 text-sm leading-relaxed text-ink-soft">{s.body}</p>
                </div>
              ))}
            </div>
            {p.format && (
              <p className="mt-4 text-xs uppercase tracking-[0.14em] text-ink-muted" data-testid="detail-format">{p.format}</p>
            )}
          </div>
        )}

        <p className="mt-6 font-display text-2xl text-ink" data-testid="detail-price">{priceLine}</p>

        {p.offer === "immersion" ? (
          intakeDone ? (
            <div className="mt-8 rounded-2xl bg-ivory-50 p-7 text-center shadow-sm ring-1 ring-ivory-400/60" data-testid="intake-received">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-sage/25 text-ink"><Check className="h-6 w-6" /></span>
              <h2 className="mt-4 font-display text-xl text-ink">Thank you — your answers are on their way to Casey.</h2>
              <p className="mt-2 text-sm text-ink-soft">When you're ready, secure your place in the immersion below. You'll book your first Zoom session right after.</p>
              <button
                type="button"
                onClick={() => navigate(`/checkout?product=${p.slug}&applied=1`)}
                data-testid="intake-continue-checkout"
                className="debossed-btn mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 font-display text-ink"
              >
                {p.cta_label || "Continue to secure your place"}
              </button>
            </div>
          ) : (
            <ImmersionIntake onSubmitted={() => setIntakeDone(true)} />
          )
        ) : p.offer === "orientation" ? (
          orientationDone ? (
            <div className="mt-8 rounded-2xl bg-ivory-50 p-7 text-center shadow-sm ring-1 ring-ivory-400/60" data-testid="orientation-received">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-sage/25 text-ink"><Check className="h-6 w-6" /></span>
              <h2 className="mt-4 font-display text-xl text-ink">Thank you — your map is on its way to Casey.</h2>
              <p className="mt-2 text-sm text-ink-soft">When you're ready, secure your Orientation below. You'll book your first Zoom session right after.</p>
              <button
                type="button"
                onClick={() => navigate(`/checkout?product=${p.slug}&intake=1`)}
                data-testid="orientation-continue-checkout"
                className="debossed-btn mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 font-display text-ink"
              >
                {p.cta_label || "Secure your Orientation"}
              </button>
            </div>
          ) : (
            <OrientationIntake onSubmitted={() => setOrientationDone(true)} />
          )
        ) : p.is_application ? (
          applied ? (
            <div className="mt-8 rounded-2xl bg-ivory-50 p-7 text-center shadow-sm ring-1 ring-ivory-400/60" data-testid="immersion-applied">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-sage/25 text-ink"><Check className="h-6 w-6" /></span>
              <h2 className="mt-4 font-display text-xl text-ink">Your application has been received.</h2>
              <p className="mt-2 text-sm text-ink-soft">Casey will read it personally and reach out about next steps.</p>
            </div>
          ) : (
            <form onSubmit={apply} className="mt-8 rounded-2xl bg-ivory-50 p-6 shadow-sm ring-1 ring-ivory-400/60" data-testid="immersion-form">
              <p className="font-display text-lg text-ink">Apply for the Immersion</p>
              <p className="mt-1 text-sm text-ink-soft">A few details so Casey can understand where you are and reach you gently.</p>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="First name" data-testid="apply-first-name" className="rounded-lg border border-ivory-400/70 bg-[#FCFBF7] p-2.5 text-sm text-ink" />
                <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="Last name" data-testid="apply-last-name" className="rounded-lg border border-ivory-400/70 bg-[#FCFBF7] p-2.5 text-sm text-ink" />
                <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" data-testid="apply-email" className="rounded-lg border border-ivory-400/70 bg-[#FCFBF7] p-2.5 text-sm text-ink" />
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (optional)" data-testid="apply-phone" className="rounded-lg border border-ivory-400/70 bg-[#FCFBF7] p-2.5 text-sm text-ink" />
              </div>
              <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} placeholder="What's bringing you here? (optional)" data-testid="apply-message" className="mt-3 w-full resize-y rounded-lg border border-ivory-400/70 bg-[#FCFBF7] p-3 text-sm text-ink" />
              {err && <p className="mt-2 text-sm text-terracotta" data-testid="apply-error">{err}</p>}
              <button type="submit" disabled={sending} data-testid="apply-submit" className="debossed-btn mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 font-display text-ink disabled:opacity-50">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Send my application
              </button>
            </form>
          )
        ) : (
          <button
            type="button"
            onClick={() => navigate(`/checkout?product=${p.slug}`)}
            data-testid="detail-cta"
            className="debossed-btn mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 font-display text-ink"
          >
            {p.cta_label}
          </button>
        )}
      </div>
    </div>
  );
}
